/**
 * OnlineApplicationsPanel — Phase 2 (Bill 2026-07-01) RAO-landing widget for the
 * PCB-Flow-owned online account-opening lists at /sites/PCB-AccountOpening.
 *
 * Sibling of LegacyApplicationsPanel — SAME landing queue, different source
 * channel (portal/JotForm intake joined across Account + Applicant + Beneficiary).
 * One click on "Import & open" calls onlineApplications.import(accountItemId) →
 * the server builds a preloaded draft cart (People/Products/Services/Beneficiaries
 * + the JotForm PDF as a regulated artifact) → navigate to the new cart.
 *
 * FSR-facing filter is server-side (711='Y' & Status='New Account', OR
 * Status in Claimed/Pending & FSR=me). Admins (SuperAdmin/Admin) additionally get
 * a "pilot: all online apps" toggle that surfaces every online app for testing.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { OnlineApplicationSummary } from '@retail-ops/api-client';
import { FileText } from '@retail-ops/panel-shell/fi';
import { Button, Pill } from '@/components/primitives';
import { PdfPopupViewer } from '@/components/common/PdfPopupViewer';
import { useOfficerCodeLookup } from '@/hooks/useOfficerCodeLookup';
import { usePersistedFlag } from '@/hooks/usePersistedFlag';
import { useAuth } from '@/contexts/AuthContext';
import { useOnlineImportHold } from '@/hooks/useOnlineImportHold';
import { useApi } from '../hooks/useApi';
import './OnlineApplicationsPanel.css';

type PanelState =
  | { kind: 'loading' }
  | { kind: 'ok'; items: ReadonlyArray<OnlineApplicationSummary> }
  | { kind: 'error'; message: string };

export function OnlineApplicationsPanel(): JSX.Element | null {
  const { client, makeSignal } = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const importHold = useOnlineImportHold();

  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  // PILOT 2026-09-11 (Bill, live smoke test): the pool toggle is for EVERY
  // role, not Admin only, and it persists - it used to reset on every
  // navigation, which read as "the checkbox blanked out and the claimed
  // applications are gone". Server: 2.43.2 opened ?pool=1 to any signed-in user.
  const [pool, setPool] = usePersistedFlag('onlineApps.showAll', false);
  const officerCodeFor = useOfficerCodeLookup();
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // Which application's submission PDF is open in the popup viewer.
  const [pdfFor, setPdfFor] = useState<OnlineApplicationSummary | null>(null);
  const loadPdf = useCallback(
    () => (pdfFor
      ? client.retail.onlineApplications.applicationPdfBlob(pdfFor.accountItemId, { pool })
      : Promise.reject(new Error('No application selected'))),
    [client, pdfFor, pool],
  );

  const load = useCallback(
    (usePool: boolean): void => {
      const signal = makeSignal();
      setState({ kind: 'loading' });
      client.retail.onlineApplications
        .list({ pool: usePool }, { signal })
        .then(
          (res): void => {
            if (!res.success) {
              setState({ kind: 'error', message: res.error ?? 'Unable to load online applications.' });
              return;
            }
            setState({ kind: 'ok', items: res.apps ?? [] });
          },
          (err: unknown): void => {
            if (err instanceof Error && err.name === 'AbortError') return;
            setState({
              kind: 'error',
              message: err instanceof Error ? err.message : 'Unable to load online applications.',
            });
          },
        );
    },
    // client + makeSignal are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    load(pool);
  }, [load, pool]);

  const handleImport = useCallback(
    (item: OnlineApplicationSummary): void => {
      setImportError(null);
      setImportingId(item.accountItemId);
      const signal = makeSignal();
      client.retail.onlineApplications
        .import(item.accountItemId, { pool }, { signal })
        .then(
          (res): void => {
            setImportingId(null);
            if (!res.success || !res.cartId) {
              setImportError(res.error ?? 'Import failed — try again.');
              return;
            }
            // Drop the imported item locally so it doesn't show twice while the
            // SP cache catches up.
            setState((prev) =>
              prev.kind === 'ok'
                ? { kind: 'ok', items: prev.items.filter((i) => i.accountItemId !== item.accountItemId) }
                : prev,
            );
            navigate(`/rao/${res.cartId}`);
          },
          (err: unknown): void => {
            setImportingId(null);
            if (err instanceof Error && err.name === 'AbortError') return;
            setImportError(err instanceof Error ? err.message : 'Import failed — try again.');
          },
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pool],
  );

  // Hide the whole widget for non-admins when there's nothing to import — keeps
  // the landing clean (an FSR with no claimed online apps sees only their carts).
  // Always rendered: with "Show all" available to everyone, an FSR whose own
  // queue is empty still needs the panel to reach the checkbox.

  return (
    <section className="rao-online-panel">
      <header className="rao-online-panel__header">
        <div>
          <h2 className="rao-online-panel__title">Online applications waiting</h2>
          <p className="rao-online-panel__sub">
            Customer applications submitted online. Click <em>Import &amp; open</em> to
            start a cart pre-filled with the applicant, product, and the signed
            application.
          </p>
          {importHold && (
            // Bill 2026-10-07 — import hold (onlineApplications.import.enabled).
            <p className="rao-online-panel__sub" role="status"><strong>{importHold}.</strong></p>
          )}
        </div>
        <div className="rao-online-panel__tools">
          {(
            <label className="rao-online-panel__pool" title="Pilot: show every online application regardless of who it is assigned to">
              <input
                type="checkbox"
                checked={pool}
                onChange={(e) => setPool(e.target.checked)}
              />
              Show all (pilot)
            </label>
          )}
          <Button variant="ghost" onClick={() => load(pool)}>
            Refresh
          </Button>
        </div>
      </header>

      {importError && (
        <div className="rao-online-panel__error" role="alert">
          {importError}
        </div>
      )}

      {state.kind === 'loading' && (
        <div className="rao-online-panel__empty">Loading online applications…</div>
      )}

      {state.kind === 'error' && (
        <div className="rao-online-panel__empty rao-online-panel__empty--error">
          {state.message}
        </div>
      )}

      {state.kind === 'ok' && state.items.length === 0 && (
        <div className="rao-online-panel__empty">
          {pool ? 'No online applications found.' : 'No online applications assigned to you.'}
        </div>
      )}

      {state.kind === 'ok' && state.items.length > 0 && (
        <ul className="rao-online-panel__list">
          {state.items.map((item) => (
            <li key={item.accountItemId} className="rao-online-panel__row">
              <div className="rao-online-panel__row-meta">
                <div className="rao-online-panel__row-name">
                  {item.primaryApplicant || item.accountName || 'Applicant'}
                </div>
                <div className="rao-online-panel__row-detail">
                  <Pill variant="info">Online portal</Pill>
                  {item.product && (
                    <span className="rao-online-panel__row-tag">{item.product}</span>
                  )}
                  {item.branch && (
                    <span className="rao-online-panel__row-tag">{item.branch}</span>
                  )}
                  {item.status && (
                    <span className="rao-online-panel__row-tag rao-online-panel__row-tag--status">
                      {item.status}
                    </span>
                  )}
                  {item.applicantCount > 1 && (
                    <span className="rao-online-panel__row-tag">
                      {item.applicantCount} applicants
                    </span>
                  )}
                  {item.beneficiaryCount > 0 && (
                    <span className="rao-online-panel__row-tag">
                      {item.beneficiaryCount} beneficiary{item.beneficiaryCount === 1 ? '' : 'ies'}
                    </span>
                  )}
                  {officerCodeFor(item.fsr) && (
                    <span
                      className="rao-online-panel__row-fsr"
                      title={`Claimed by ${item.fsr}`}
                      aria-label={`Claimed by ${item.fsr}`}
                    >
                      {officerCodeFor(item.fsr)}
                    </span>
                  )}
                  {/* Bill 2026-09-11: a plain PDF icon, no label, no vendor
                      name. Opens the submission in the in-app viewer. Absent
                      when no PDF is linked. */}
                  {item.hasPdfLink && (
                    <button
                      type="button"
                      className="rao-online-panel__row-pdf"
                      title="Open Application Submission PDF"
                      aria-label="Open Application Submission PDF"
                      onClick={() => setPdfFor(item)}
                    >
                      <FileText size={14} aria-hidden />
                    </button>
                  )}
                </div>
              </div>
              <div className="rao-online-panel__row-actions">
                <Button
                  variant="primary"
                  onClick={() => handleImport(item)}
                  disabled={importHold !== null || importingId === item.accountItemId}
                  title={importHold ?? undefined}
                >
                  {importingId === item.accountItemId ? 'Importing…' : 'Import & open'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <PdfPopupViewer
        open={pdfFor !== null}
        title={pdfFor ? `Application submission — ${pdfFor.primaryApplicant || pdfFor.accountName || 'Applicant'}` : 'Application submission'}
        load={loadPdf}
        onClose={() => setPdfFor(null)}
      />
    </section>
  );
}
